import { getErrorAction } from './errorMapper'
import { errorMessageAdd, successMessageAdd } from './toasts'
import uniqueId from 'lodash/uniqueId'
import moment from 'moment'
import URL from 'url'
import find from 'lodash/find'
import omit from 'lodash/omit'
import { createReducer } from './reducerUtils'
import { mergeMap, map, catchError } from 'rxjs/operators'
import { ofType } from 'redux-observable'
import { of } from 'rxjs'

// Actions
export const TASK_LINKS_LOAD = 'TASK_LINKS_LOAD'
export const TASK_LINKS_LOAD_SUCCESS = 'TASK_LINKS_LOAD_SUCCESS'
export const TASK_LINKS_LOAD_FAILURE = 'TASK_LINKS_LOAD_FAILURE'
export const TASK_LINKS_DELETE_LINK = 'TASK_LINKS_DELETE_LINK'
export const TASK_LINKS_DELETE_LINK_SUCCESS = 'TASK_LINKS_DELETE_LINK_SUCCESS'
export const TASK_LINKS_DELETE_LINK_FAILURE = 'TASK_LINKS_DELETE_LINK_FAILURE'

export const TASK_LINK_ADD = 'TASK_LINK_ADD'
export const TASK_LINK_ADD_SUCCESS = 'TASK_LINK_ADD_SUCCESS'
export const TASK_LINK_ADD_FAILURE = 'TASK_LINK_ADD_FAILURE'

export const GENERATED_TASK_LINK_ID_PREFIX = 'unsaved-link-'

// Action creators
export const taskLinksLoad = (taskId) => ({
  type: TASK_LINKS_LOAD,
  payload: {
    taskId
  }
})

export const taskLinksLoadSuccess = (taskId, taskLinks) => ({
  type: TASK_LINKS_LOAD_SUCCESS,
  payload: { taskId, taskLinks }
})
export const taskLinksLoadFailure = payload => ({ type: TASK_LINKS_LOAD_FAILURE, payload, error: true })
export const taskLinksDeleteLink = (taskId, linkId) => ({
  type: TASK_LINKS_DELETE_LINK,
  payload: {
    taskId,
    linkId
  }
})

export const taskLinksDeleteLinkSuccess = (taskId, linkId) => ({
  type: TASK_LINKS_DELETE_LINK_SUCCESS,
  payload: { taskId, linkId }
})

export const taskLinksDeleteLinkFailure = payload => ({
  type: TASK_LINKS_DELETE_LINK_FAILURE,
  payload,
  error: true
})

export const taskLinkAdd = (taskId, name, url) => ({
  type: TASK_LINK_ADD,
  payload: {
    taskId,
    name,
    url,
    tempLinkId: uniqueId(GENERATED_TASK_LINK_ID_PREFIX)
  }
})
export const taskLinkAddSuccess = (taskId, tempLinkId, taskLink) => ({
  type: TASK_LINK_ADD_SUCCESS,
  payload: { taskId, tempLinkId, taskLink }
})
export const taskLinkAddFailure = payload => ({ type: TASK_LINK_ADD_FAILURE, payload, error: true })

export const correctUrlProtocol = linkUrl => {
  const protocolMissing = !URL.parse(linkUrl, true, true).protocol && linkUrl.indexOf('//') !== 0

  return protocolMissing ? `http://${linkUrl}` : linkUrl
}

export const mapTaskLink = (taskLink) => {
  return {
    id: taskLink.id,
    name: taskLink.name,
    url: correctUrlProtocol(taskLink.url),
    createdOn: taskLink.createdOn
  }
}

// Epics
export const taskLinksLoadEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(TASK_LINKS_LOAD),
    mergeMap((action) =>
      vapiClient
        .getTaskLinks(action.payload.taskId).pipe(
          map((ajaxResponse) => ajaxResponse.response.map(mapTaskLink)),
          map((taskLinks) => taskLinksLoadSuccess(action.payload.taskId, taskLinks)),
          catchError(error => of(getErrorAction(error, taskLinksLoadFailure, action.payload)))
        )
    )
  )

export const taskLinksLoadFailureEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(TASK_LINKS_LOAD_FAILURE),
    map(action => {
      const taskName = state$.value.tasks[action.payload.taskId].name
      return errorMessageAdd(
        localize(state$.value.resources, 'taskBoards.Msg_UnableToLoadTaskLinks')
          .replace('{taskName}', taskName)
      )
    })
  )

export const taskLinkAddEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(TASK_LINK_ADD),
    mergeMap((action) =>
      vapiClient
        .addTaskLink(action.payload.taskId, action.payload.url, action.payload.name).pipe(
          map((ajaxResponse) => mapTaskLink(ajaxResponse.response)),
          map((taskLink) => taskLinkAddSuccess(action.payload.taskId, action.payload.tempLinkId, taskLink)),
          catchError(error => of(getErrorAction(error, taskLinkAddFailure, action.payload)))
        )
    )
  )

export const taskLinkAddFailureEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(TASK_LINK_ADD_FAILURE),
    map(action => {
      const taskName = state$.value.tasks[action.payload.taskId].name
      return errorMessageAdd(
        localize(state$.value.resources, 'taskBoards.Msg_UnableToSaveTaskLink')
          .replace('{taskName}', taskName)
      )
    })
  )

export const taskLinksDeleteLinkEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(TASK_LINKS_DELETE_LINK),
    mergeMap((action) => {
      return vapiClient
        .deleteTaskLink(action.payload.taskId, action.payload.linkId).pipe(
          map((ajaxResponse) => {
            return taskLinksDeleteLinkSuccess(action.payload.taskId, action.payload.linkId)
          }),
          catchError(error => of(getErrorAction(error, taskLinksDeleteLinkFailure, action.payload)))
        )
    })
  )

export const taskLinksDeleteLinkFailureEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(TASK_LINKS_DELETE_LINK_FAILURE),
    map((action) => {
      const taskName = state$.value.tasks[action.payload.taskId].name
      return errorMessageAdd(
        localize(state$.value.resources, 'taskBoards.Msg_TaskLinkDeleteFailed')
          .replace('{taskName}', taskName)
      )
    })
  )

export const taskLinksDeleteLinkSuccessEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(TASK_LINKS_DELETE_LINK_SUCCESS),
    map((action) => {
      return successMessageAdd(
        localize(state$.value.resources, 'taskBoards.Msg_TaskLinkDeleted')
      )
    })
  )

// Reducer
const taskLinkAddReducer = (state, { taskId, tempLinkId, url, name }) => ({
  ...state,
  [taskId]: [
    ...(state[taskId] || []),
    mapTaskLink({
      id: tempLinkId,
      url,
      name,
      createdOn: moment.utc().toISOString()
    })
  ]
})

const taskLinkAddSuccessReducer = (state, { taskId, tempLinkId, taskLink }) => ({
  ...state,
  [taskId]: [ ...state[taskId].filter((link) => link.id !== tempLinkId), taskLink ]
})

const taskLinkAddFailureReducer = (state, { taskId, tempLinkId }) => ({
  ...state,
  [taskId]: state[taskId].filter((link) => link.id !== tempLinkId)
})

const taskLinksLoadSuccessReducer = (state, { taskId, taskLinks }) => ({ ...state, [taskId]: taskLinks })

const taskLinksLoadFailureReducer = (state, { taskId }) => ({ ...state, [taskId]: [] })

const taskLinksDeleteReducer = (state, { taskId, linkId }) => ({
  ...state,
  [taskId]: state[taskId].filter(link => link.id !== linkId),
  deletedLinks: {
    ...state.deletedLinks,
    [linkId]: find(state[taskId], link => link.id === linkId)
  }
})

const taskLinksDeleteSuccessReducer = (state, { linkId }) => ({
  ...state,
  deletedLinks: omit(state.deletedLinks, [linkId])
})

const taskLinksDeleteFailureReducer = (state, { taskId, linkId }) => {
  const deletedLink = state.deletedLinks[linkId]
  return {
    ...state,
    [taskId]: [...state[taskId], deletedLink],
    deletedLinks: omit(state.deletedLinks, [linkId])
  }
}

const taskLinksHandlerMapping = {
  [TASK_LINK_ADD]: taskLinkAddReducer,
  [TASK_LINK_ADD_SUCCESS]: taskLinkAddSuccessReducer,
  [TASK_LINK_ADD_FAILURE]: taskLinkAddFailureReducer,
  [TASK_LINKS_LOAD_SUCCESS]: taskLinksLoadSuccessReducer,
  [TASK_LINKS_LOAD_FAILURE]: taskLinksLoadFailureReducer,
  [TASK_LINKS_DELETE_LINK]: taskLinksDeleteReducer,
  [TASK_LINKS_DELETE_LINK_SUCCESS]: taskLinksDeleteSuccessReducer,
  [TASK_LINKS_DELETE_LINK_FAILURE]: taskLinksDeleteFailureReducer
}

const taskLinksInitialState = { deletedLinks: {} }
export const taskLinksReducer = createReducer(taskLinksInitialState, taskLinksHandlerMapping)

export default taskLinksReducer



// WEBPACK FOOTER //
// ./src/store/taskLinks.js